import { c as _c } from "react/compiler-runtime";
import CommonAnimationHeight from "@/components/utils/animation/CommonAnimationHeight";
import { ApiWaveParticipationIdentitySubmissionAllowDuplicates } from "@/generated/models/ApiWaveParticipationIdentitySubmissionAllowDuplicates";
import { ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted } from "@/generated/models/ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted";
import type { ApiWaveParticipationSubmissionStrategy } from "@/generated/models/ApiWaveParticipationSubmissionStrategy";
import { ApiWaveParticipationSubmissionStrategyType } from "@/generated/models/ApiWaveParticipationSubmissionStrategyType";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { WAVE_IDENTITY_DUPLICATES_COPY, WAVE_IDENTITY_WHO_CAN_BE_SUBMITTED_COPY } from "@/helpers/waves/wave-submission-strategy.helpers";
enum CreateWaveSubmissionMode {
  STANDARD = "STANDARD",
  IDENTITY = "IDENTITY",
}
const createDefaultIdentitySubmissionStrategy = (): ApiWaveParticipationSubmissionStrategy => ({
  type: ApiWaveParticipationSubmissionStrategyType.Identity,
  config: {
    duplicates: ApiWaveParticipationIdentitySubmissionAllowDuplicates.NeverAllow,
    who_can_be_submitted: ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.Everyone
  }
});
type SubmissionOptionSection = "who-can-be-submitted" | "duplicate-submissions";
type SubmissionModeOptionSection = "submission-mode";
type SubmissionOptionRowProps<T extends string> = {
  readonly type: T;
  readonly selected: T;
  readonly label: string;
  readonly description?: string;
  readonly titleId: string;
  readonly descriptionId?: string;
  readonly groupName: string;
  readonly onChange: (type: T) => void;
};
const getOptionDescriptionId = (section: SubmissionOptionSection | SubmissionModeOptionSection, option: string) => `${section}-${option.toLowerCase()}-description`;
const getOptionTitleId = (section: SubmissionOptionSection | SubmissionModeOptionSection, option: string) => `${section}-${option.toLowerCase()}-title`;
function SubmissionOptionRow(t0) {
  const $ = _c(30);
  const {
    type,
    selected,
    label,
    description,
    titleId,
    descriptionId,
    groupName,
    onChange
  } = t0;
  const isSelected = selected === type;
  const wrapperClasses = isSelected ? "tw-border-primary-400 tw-bg-primary-500/5 tw-ring-primary-500/30" : "tw-border-white/5 tw-bg-iron-900 tw-ring-white/5 hover:tw-border-white/10 hover:tw-bg-iron-800 hover:tw-ring-white/10";
  const labelClasses = isSelected ? "tw-text-white" : "tw-text-iron-300 group-hover:tw-text-white";
  const descriptionClasses = isSelected ? "tw-text-iron-300" : "tw-text-iron-500";
  const hasDescription = description !== undefined;
  const t1 = `${wrapperClasses} tw-group tw-flex tw-w-full tw-cursor-pointer tw-gap-x-3 tw-rounded-xl tw-border tw-border-solid tw-px-3 tw-py-3 tw-shadow-inner tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out ${hasDescription ? "tw-items-start" : "tw-items-center"}`;
  let t2;
  if ($[0] !== onChange || $[1] !== type) {
    t2 = () => onChange(type);
    $[0] = onChange;
    $[1] = type;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== descriptionId || $[4] !== groupName || $[5] !== isSelected || $[6] !== t2 || $[7] !== titleId) {
    t3 = <input type="radio" name={groupName} checked={isSelected} onChange={t2} aria-labelledby={titleId} aria-describedby={descriptionId} className="tw-peer tw-sr-only" />;
    $[3] = descriptionId;
    $[4] = groupName;
    $[5] = isSelected;
    $[6] = t2;
    $[7] = titleId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = `tw-flex tw-h-4 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition tw-duration-300 tw-ease-out peer-focus-visible:tw-ring-2 peer-focus-visible:tw-ring-primary-500 peer-focus-visible:tw-ring-offset-2 peer-focus-visible:tw-ring-offset-iron-950 ${isSelected ? "tw-border-primary-400 tw-bg-primary-500/10" : "tw-border-iron-600 tw-bg-transparent group-hover:tw-border-iron-500"} ${hasDescription ? "tw-mt-1" : ""}`;
  const t5 = `tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-400 tw-transition tw-duration-200 ${isSelected ? "tw-scale-100" : "tw-scale-0"}`;
  let t6;
  if ($[9] !== t5) {
    t6 = <span className={t5} />;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <span aria-hidden="true" className={t4}>{t6}</span>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = `${labelClasses} tw-min-h-4 tw-text-sm tw-font-semibold`;
  let t9;
  if ($[14] !== label || $[15] !== t8 || $[16] !== titleId) {
    t9 = <span id={titleId} className={t8}>{label}</span>;
    $[14] = label;
    $[15] = t8;
    $[16] = titleId;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== description || $[19] !== descriptionClasses || $[20] !== descriptionId) {
    t10 = description && <span id={descriptionId} className={`${descriptionClasses} tw-mt-1 tw-text-xs tw-font-medium tw-leading-4`}>{description}</span>;
    $[18] = description;
    $[19] = descriptionClasses;
    $[20] = descriptionId;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <div className="tw-flex tw-min-w-0 tw-flex-col">{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t1 || $[26] !== t11 || $[27] !== t3 || $[28] !== t7) {
    t12 = <label className={t1}>{t3}{t7}{t11}</label>;
    $[25] = t1;
    $[26] = t11;
    $[27] = t3;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
export default function CreateWaveDropsSubmissionMode(t0) {
  const $ = _c(37);
  const {
    submissionStrategy,
    errors,
    onChange
  } = t0;
  const selectedMode = submissionStrategy === null ? CreateWaveSubmissionMode.STANDARD : CreateWaveSubmissionMode.IDENTITY;
  let t1;
  if ($[0] !== errors) {
    t1 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.DROPS_SUBMISSION_STRATEGY_INVALID);
    $[0] = errors;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const showSubmissionStrategyError = t1;
  let t2;
  if ($[2] !== onChange || $[3] !== submissionStrategy) {
    t2 = mode => {
      if (mode === CreateWaveSubmissionMode.STANDARD) {
        onChange(null);
        return;
      }
      onChange(submissionStrategy ?? createDefaultIdentitySubmissionStrategy());
    };
    $[2] = onChange;
    $[3] = submissionStrategy;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onSubmissionModeChange = t2;
  let t3;
  if ($[5] !== onChange || $[6] !== submissionStrategy) {
    t3 = whoCanBeSubmitted => {
      if (!submissionStrategy) {
        return;
      }
      onChange({
        ...submissionStrategy,
        config: {
          ...submissionStrategy.config,
          who_can_be_submitted: whoCanBeSubmitted
        }
      });
    };
    $[5] = onChange;
    $[6] = submissionStrategy;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const onWhoCanBeSubmittedChange = t3;
  let t4;
  if ($[8] !== onChange || $[9] !== submissionStrategy) {
    t4 = duplicates => {
      if (!submissionStrategy) {
        return;
      }
      onChange({
        ...submissionStrategy,
        config: {
          ...submissionStrategy.config,
          duplicates
        }
      });
    };
    $[8] = onChange;
    $[9] = submissionStrategy;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const onDuplicatesChange = t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[11] !== onSubmissionModeChange || $[12] !== selectedMode) {
    const submissionModeOptions = [{
      type: CreateWaveSubmissionMode.STANDARD,
      label: "Standard drops"
    }, {
      type: CreateWaveSubmissionMode.IDENTITY,
      label: "Identity nominations"
    }];
    t7 = "tw-flex tw-flex-col tw-gap-y-6";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div><p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">Submission type</p><p className="tw-mb-0 tw-mt-2 tw-text-sm tw-font-medium tw-text-iron-400">Choose whether participants submit drops or nominate identities.</p></div>;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t5 = "tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2";
    t6 = submissionModeOptions.map(option => <SubmissionOptionRow key={option.type} type={option.type} selected={selectedMode} label={option.label} titleId={getOptionTitleId("submission-mode", option.type)} groupName="submission-mode" onChange={onSubmissionModeChange} />);
    $[11] = onSubmissionModeChange;
    $[12] = selectedMode;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[18] !== t5 || $[19] !== t6) {
    t9 = <div className={t5}>{t6}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== onDuplicatesChange || $[22] !== onWhoCanBeSubmittedChange || $[23] !== submissionStrategy) {
    t10 = submissionStrategy && <div className="tw-flex tw-flex-col tw-gap-y-6"><div><p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-100">Which identities can a participant submit?</p><div className="tw-mt-3 tw-grid tw-grid-cols-1 tw-gap-3">{(Object.values(ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted) as ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted[]).map(option_0 => {
            const titleId = getOptionTitleId("who-can-be-submitted", option_0);
            const descriptionId = getOptionDescriptionId("who-can-be-submitted", option_0);
            return <SubmissionOptionRow key={option_0} type={option_0} selected={submissionStrategy.config.who_can_be_submitted} label={WAVE_IDENTITY_WHO_CAN_BE_SUBMITTED_COPY[option_0].label} description={WAVE_IDENTITY_WHO_CAN_BE_SUBMITTED_COPY[option_0].description} titleId={titleId} descriptionId={descriptionId} groupName="who-can-be-submitted" onChange={onWhoCanBeSubmittedChange} />;
          })}</div></div><div><p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-100">When can the same identity be submitted again?</p><div className="tw-mt-3 tw-grid tw-grid-cols-1 tw-gap-3">{(Object.values(ApiWaveParticipationIdentitySubmissionAllowDuplicates) as ApiWaveParticipationIdentitySubmissionAllowDuplicates[]).map(option_1 => {
            const titleId_0 = getOptionTitleId("duplicate-submissions", option_1);
            const descriptionId_0 = getOptionDescriptionId("duplicate-submissions", option_1);
            return <SubmissionOptionRow key={option_1} type={option_1} selected={submissionStrategy.config.duplicates} label={WAVE_IDENTITY_DUPLICATES_COPY[option_1].label} description={WAVE_IDENTITY_DUPLICATES_COPY[option_1].description} titleId={titleId_0} descriptionId={descriptionId_0} groupName="duplicate-submissions" onChange={onDuplicatesChange} />;
          })}</div></div></div>;
    $[21] = onDuplicatesChange;
    $[22] = onWhoCanBeSubmittedChange;
    $[23] = submissionStrategy;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = <CommonAnimationHeight>{t10}</CommonAnimationHeight>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== showSubmissionStrategyError) {
    t12 = showSubmissionStrategyError && <div className="tw-text-sm tw-font-medium tw-text-error">Complete the identity nomination settings before continuing.</div>;
    $[27] = showSubmissionStrategyError;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12) {
    t13 = <CommonAnimationHeight>{t12}</CommonAnimationHeight>;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t13 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t14 = <div className={t7}>{t8}{t9}{t11}{t13}</div>;
    $[31] = t11;
    $[32] = t13;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
}